/**
 * SPEI checkout colour literals — the `v3-mx` flow's own payment surface.
 *
 * Same rule as [`finish.tokens.ts`](./finish.tokens.ts): no raw colour lives in a component
 * file. These are only the literals the SPEI screens introduce (the transfer-coordinates
 * panel, the copy-to-clipboard buttons, the "pending" notice); everything the Stripe
 * checkout already had a token for is re-used from `FINISH` / `CFG` rather than re-declared.
 *
 * Registered through `themed()`, so each value becomes a `var(--ows-cfg-spei-…)` custom
 * property and the panel follows the studio's light/dark switch like the rest of the wizard.
 */

import { themed } from '../../../../theme/themeRegistry';
import { CFG, CFG_CHROME } from '../../../../theme/configurator-tokens';

/**
 * Fill for a field INSIDE an expanded payment method, as literals per theme.
 *
 * The method options are a raised surface (`FINISH.methodRowBg`, `#18181b`) sitting on the
 * darker checkout pane, so a transparent field reads as part of the card rather than as
 * something to type in. These sink it back below the card instead — dark theme goes darker than
 * the pane itself; light theme inverts the relationship (a faint grey inset on the white card),
 * because "very dark" there would be a hole, not a field.
 *
 * Kept as plain literals, and NOT run through `themed()`, because Stripe renders its Elements in
 * a **cross-origin iframe** that cannot resolve our `var(--ows-…)` custom properties: the card
 * inputs have to be handed the concrete value (see `data/payments.ts`). The themed token below
 * feeds the SPEI field from the same pair, so the two rails can never drift apart.
 */
export const MX_FIELD_BG = { dark: '#0b0b0e', light: '#f4f5f8' } as const;

export const SPEI = themed('cfgSpei', {
  /** See {@link MX_FIELD_BG}. Its light value is authored in `themePalette.ts`. */
  fieldBg: MX_FIELD_BG.dark,

  // Transfer-coordinates panel (the card holding the CLABE, reference, beneficiary…).
  panelBg: CFG.card, // #17181c — sits on the darker pane backdrop
  panelBorder: CFG.hairline, // hairline frame
  rowDivider: CFG.hairlineSoft, // rule between coordinate rows
  rowLabel: CFG.textMuted, // "CLABE" / "Reference" caption
  rowValue: CFG.foreground, // the value itself

  // Copy-to-clipboard button (ghost square that flips to a green tick once copied).
  copyBg: CFG.wash,
  copyBgHover: CFG.washHover,
  copyBorder: CFG.hairlineStrong,
  copyIcon: CFG.textDim,
  copiedIcon: CFG.success, // tick + "Copied" flash
  copiedBg: 'rgba(23, 201, 100, 0.12)',
  copiedBorder: 'rgba(23, 201, 100, 0.32)',

  // The amount, called out above the coordinates.
  amountLabel: CFG.textMuted,
  amountValue: CFG.foreground,

  // "We'll confirm once the transfer lands" notice + the expiry line.
  noticeBg: 'rgba(255, 121, 27, 0.08)', // brand-tinted, matches the wizard's accent
  noticeBorder: 'rgba(255, 121, 27, 0.26)',
  noticeIcon: CFG.brand,
  noticeText: CFG.textDim,
  expiryText: CFG.textFaint,

  // Numbered "how to pay" steps.
  stepIndexBg: CFG_CHROME.chipBg,
  stepIndexText: CFG.textDim,
  stepText: CFG.textMuted,
} as const);

/**
 * SPEI's own brand colours for the wordmark.
 *
 * Deliberately NOT run through `themed()`. These belong to a third-party payment rail, so they
 * must not be re-derived by our light/accent palette machinery the way our own tokens are —
 * the variant is chosen explicitly in React via `useThemeMode()`, the same approach
 * `CFG_STRIPE` / `CFG_STRIPE_LIGHT` take for the Stripe Elements appearance.
 *
 * Two variants exist for a contrast reason, not a stylistic one: the brand navy is `#343084`,
 * which lands at **1.76:1** on the dark checkout pane and is effectively unreadable. That is
 * what the old white plate behind the mark was compensating for. Dropping the plate means the
 * dark variant has to carry a lightened navy instead — same indigo hue, 5.04:1 — while the
 * orange (8.8:1 on dark) is the true brand value in both.
 */
export const SPEI_BRAND_LIGHT = {
  /** True SPEI navy — 11:1 on white. */
  navy: '#343084',
  orange: '#ff9400',
} as const;

export const SPEI_BRAND_DARK = {
  /** Lightened navy so the "SP" survives on the dark pane without a backing plate (5.04:1). */
  navy: '#7d76d6',
  orange: '#ff9400',
} as const;

/**
 * The plate the card-network marks are printed on.
 *
 * Not run through `themed()` for the same reason as `SPEI_BRAND_*`: it belongs to a third party's
 * presentation rules, and re-deriving it through our light/accent machinery would put a card
 * network on a background its owner does not allow. Visa's mark is `#1434cb` on nothing, which
 * lands around 1.3:1 on the dark option card — the plate is what makes one official asset serve
 * both studio themes. Every other colour in those marks comes from the assets themselves.
 */
export const CARD_BRAND = {
  plate: '#ffffff',
} as const;
